// comment-ca-marche.tsx
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation /
// t()), y compris les balises <Head> (title, meta description, og:*).
// Ajout d'un sélecteur de langue flottant en haut à droite (même
// système que landing.tsx et les écrans publics Market : i18n.changeLanguage
// + saveLanguage), car cet écran public n'a pas de navbar existante.
// Icônes, couleurs, routes et og:url / canonical (URLs fixes) restent
// codés en dur, inchangés.

import React, { useCallback, useState } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity,
  useWindowDimensions, Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import HarmoniaLogo from '../components/HarmoniaLogo';
import { saveLanguage } from '../services/storage';

// ─── Palette (identique à index.tsx pour cohérence visuelle) ────────────────
const P = {
  purple:     '#6B21A8',
  purpleL:    '#7C3AED',
  purpleSoft: '#F3EEFF',
  gold:       '#F59E0B',
  goldSoft:   '#FFF8E8',
  blue:       '#2563EB',
  blueSoft:   '#EFF6FF',
  green:      '#059669',
  greenSoft:  '#ECFDF5',
  bg:         '#FAFAFA',
  white:      '#FFFFFF',
  text:       '#111827',
  textSub:    '#6B7280',
  textLight:  '#9CA3AF',
  border:     '#E5E7EB',
};

// ─── Langues disponibles pour le sélecteur (même liste que landing.tsx) ────
const LANGUAGES = [
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' },
  { code: 'pt', flag: '🇵🇹', label: 'PT' },
];

// ─── Métadonnées des disciplines (icônes, couleurs, IDs — non traduisibles) ──
const DISCIPLINES_META = [
  { id: 'strategie',    icon: 'extension-puzzle-outline' as const, accent: '#A78BFA' },
  { id: 'connaissance', icon: 'bulb-outline' as const,             accent: '#38BDF8' },
  { id: 'arts',         icon: 'color-palette-outline' as const,    accent: '#F472B6' },
  { id: 'performance',  icon: 'mic-outline' as const,               accent: '#FBBF24' },
  { id: 'musique',      icon: 'musical-notes-outline' as const,     accent: '#2DD4BF' },
  { id: 'artisanat',    icon: 'construct-outline' as const,         accent: '#F59E0B' },
];

// ─── Composants réutilisables ────────────────────────────────────────────────
const Chip = ({ label, color, bg }: { label: string; color: string; bg: string }) => (
  <View style={[styles.chip, { backgroundColor: bg }]}>
    <Text style={[styles.chipTxt, { color }]}>{label}</Text>
  </View>
);

const StepBlock = ({ number, title, desc }: { number: string; title: string; desc: string }) => (
  <View style={styles.stepBlock}>
    <View style={styles.stepNumberCircle}>
      <Text style={styles.stepNumberTxt}>{number}</Text>
    </View>
    <View style={{ flex: 1 }}>
      <Text style={styles.stepTitle}>{title}</Text>
      <Text style={styles.stepDesc}>{desc}</Text>
    </View>
  </View>
);

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <TouchableOpacity style={styles.faqItem} onPress={() => setOpen(!open)} activeOpacity={0.8}>
      <View style={styles.faqHeader}>
        <Text style={styles.faqQ}>{q}</Text>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={P.textSub} />
      </View>
      {open && <Text style={styles.faqA}>{a}</Text>}
    </TouchableOpacity>
  );
};

// ── Sélecteur de langue flottant (même système que landing.tsx) ──
function FloatingLanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const currentLanguage = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleChangeLanguage = useCallback(async (code: string) => {
    await i18n.changeLanguage(code);
    await saveLanguage(code);
    setOpen(false);
  }, [i18n]);

  return (
    <>
      <TouchableOpacity
        onPress={() => setOpen((v) => !v)}
        style={langStyles.trigger}
        activeOpacity={0.8}
      >
        <Text style={langStyles.flag}>{currentLanguage.flag}</Text>
        <Text style={langStyles.label}>{currentLanguage.label}</Text>
        <Ionicons name="chevron-down" size={12} color="#fff" />
      </TouchableOpacity>

      {open && (
        <TouchableOpacity
          style={langStyles.backdrop}
          activeOpacity={1}
          onPress={() => setOpen(false)}
        >
          <View style={langStyles.menu}>
            {LANGUAGES.map((lang) => (
              <TouchableOpacity
                key={lang.code}
                onPress={() => handleChangeLanguage(lang.code)}
                style={[
                  langStyles.menuItem,
                  lang.code === currentLanguage.code && langStyles.menuItemActive,
                ]}
              >
                <Text style={langStyles.menuFlag}>{lang.flag}</Text>
                <Text style={langStyles.menuLabel}>{lang.label}</Text>
                {lang.code === currentLanguage.code && (
                  <Ionicons name="checkmark" size={16} color="#F59E0B" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      )}
    </>
  );
}

export default function ComentCaMarcheScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isWebLarge = width > 800;
  const go = () => router.push('/login');

  const DISCIPLINES = DISCIPLINES_META.map((meta) => ({
    ...meta,
    label:   t(`commentCaMarcheScreen.disciplines.${meta.id}.label`),
    tagline: t(`commentCaMarcheScreen.disciplines.${meta.id}.tagline`),
    desc:    t(`commentCaMarcheScreen.disciplines.${meta.id}.desc`),
  }));

  return (
    <View style={styles.root}>
      {/*
        Contenu SEO propre à cette route : title + description uniques,
        distincts de la page d'accueil, décrivant précisément le
        fonctionnement de la plateforme pour les moteurs de recherche.
      */}
      <Head>
        <title>{t('commentCaMarcheScreen.seo.title')}</title>
        <meta
          name="description"
          content={t('commentCaMarcheScreen.seo.description')}
        />
        <meta property="og:title" content={t('commentCaMarcheScreen.seo.title')} />
        <meta
          property="og:description"
          content={t('commentCaMarcheScreen.seo.ogDescription')}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.harmoniaworld.world/comment-ca-marche" />
        <link rel="canonical" href="https://www.harmoniaworld.world/comment-ca-marche" />
      </Head>

      <FloatingLanguagePicker />

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>

        {/* ── En-tête ── */}
        <LinearGradient colors={[P.purple, '#3B0764']} style={styles.hero}>
          <View style={styles.navLogoRow}>
            <HarmoniaLogo size={30} showText={false} theme="light" />
            <Text style={styles.navBrand}>Harmonia</Text>
          </View>
          <Text style={[styles.heroTitle, isWebLarge && { fontSize: 46 }]}>
            {t('commentCaMarcheScreen.hero.titleLine1')}{'\n'}{t('commentCaMarcheScreen.hero.titleLine2')}
          </Text>
          <Text style={styles.heroSub}>
            {t('commentCaMarcheScreen.hero.sub')}
          </Text>
        </LinearGradient>

        {/* ── SECTION 1 : Présentation générale ── */}
        <View style={[styles.section, isWebLarge && styles.sectionWeb]}>
          <Chip label={t('commentCaMarcheScreen.presentation.chip')} color={P.purple} bg={P.purpleSoft} />
          <Text style={styles.sectionTitle}>{t('commentCaMarcheScreen.presentation.title')}</Text>
          <Text style={styles.sectionText}>
            {t('commentCaMarcheScreen.presentation.p1')}
          </Text>
          <Text style={styles.sectionText}>
            {t('commentCaMarcheScreen.presentation.p2')}
          </Text>
        </View>

        {/* ── SECTION 2 : Créer un compte ── */}
        <View style={[styles.section, isWebLarge && styles.sectionWeb]}>
          <Chip label={t('commentCaMarcheScreen.gettingStarted.chip')} color={P.blue} bg={P.blueSoft} />
          <Text style={styles.sectionTitle}>{t('commentCaMarcheScreen.gettingStarted.title')}</Text>
          <View style={styles.stepsWrap}>
            <StepBlock number="1" title={t('commentCaMarcheScreen.gettingStarted.step1.title')}
              desc={t('commentCaMarcheScreen.gettingStarted.step1.desc')} />
            <StepBlock number="2" title={t('commentCaMarcheScreen.gettingStarted.step2.title')}
              desc={t('commentCaMarcheScreen.gettingStarted.step2.desc')} />
            <StepBlock number="3" title={t('commentCaMarcheScreen.gettingStarted.step3.title')}
              desc={t('commentCaMarcheScreen.gettingStarted.step3.desc')} />
            <StepBlock number="4" title={t('commentCaMarcheScreen.gettingStarted.step4.title')}
              desc={t('commentCaMarcheScreen.gettingStarted.step4.desc')} />
          </View>
        </View>

        {/* ── SECTION 3 : Le Centre des Talents ── */}
        <View style={[styles.sectionDark]}>
          <View style={[styles.sectionPadDark, isWebLarge && styles.sectionWeb]}>
            <Chip label={t('commentCaMarcheScreen.talentHub.chip')} color="#A78BFA" bg="rgba(167,139,250,0.15)" />
            <Text style={styles.sectionTitleLight}>{t('commentCaMarcheScreen.talentHub.title')}</Text>
            <Text style={styles.sectionTextLight}>
              {t('commentCaMarcheScreen.talentHub.p1')}
            </Text>
            <Text style={[styles.sectionTextLight, { fontStyle: 'italic' }]}>
              {t('commentCaMarcheScreen.talentHub.p2')}
            </Text>

            <View style={styles.discGrid}>
              {DISCIPLINES.map((d) => (
                <View key={d.id} style={styles.discCard}>
                  <View style={[styles.discIconWrap, { borderColor: d.accent + '60' }]}>
                    <Ionicons name={d.icon} size={20} color={d.accent} />
                  </View>
                  <Text style={styles.discLabel}>{d.label}</Text>
                  <Text style={[styles.discTagline, { color: d.accent }]}>{d.tagline}</Text>
                  <Text style={styles.discDesc}>{d.desc}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* ── SECTION 4 : Fil d'actualité et communauté ── */}
        <View style={[styles.section, isWebLarge && styles.sectionWeb]}>
          <Chip label={t('commentCaMarcheScreen.community.chip')} color={P.green} bg={P.greenSoft} />
          <Text style={styles.sectionTitle}>{t('commentCaMarcheScreen.community.title')}</Text>
          <Text style={styles.sectionText}>
            {t('commentCaMarcheScreen.community.p1')}
          </Text>
          <Text style={styles.sectionText}>
            {t('commentCaMarcheScreen.community.p2')}
          </Text>
        </View>

        {/* ── SECTION 5 : Boutiques et sites pro (priorité SEO) ── */}
        <View style={styles.sectionDark}>
          <View style={[styles.sectionPadDark, isWebLarge && styles.sectionWeb]}>
            <Chip label={t('commentCaMarcheScreen.proSolution.chip')} color="#F59E0B" bg="rgba(245,158,11,0.15)" />
            <Text style={styles.sectionTitleLight}>{t('commentCaMarcheScreen.proSolution.title')}</Text>
            <Text style={styles.sectionTextLight}>
              {t('commentCaMarcheScreen.proSolution.p1')}
            </Text>

            <View style={styles.stepsWrap}>
              <StepBlock number="1" title={t('commentCaMarcheScreen.proSolution.step1.title')}
                desc={t('commentCaMarcheScreen.proSolution.step1.desc')} />
              <StepBlock number="2" title={t('commentCaMarcheScreen.proSolution.step2.title')}
                desc={t('commentCaMarcheScreen.proSolution.step2.desc')} />
              <StepBlock number="3" title={t('commentCaMarcheScreen.proSolution.step3.title')}
                desc={t('commentCaMarcheScreen.proSolution.step3.desc')} />
              <StepBlock number="4" title={t('commentCaMarcheScreen.proSolution.step4.title')}
                desc={t('commentCaMarcheScreen.proSolution.step4.desc')} />
            </View>
          </View>
        </View>

        {/* ── SECTION 6 : Statut PRO ── */}
        <View style={[styles.section, isWebLarge && styles.sectionWeb]}>
          <Chip label={t('commentCaMarcheScreen.proStatus.chip')} color={P.gold} bg={P.goldSoft} />
          <Text style={styles.sectionTitle}>{t('commentCaMarcheScreen.proStatus.title')}</Text>
          <Text style={styles.sectionText}>
            {t('commentCaMarcheScreen.proStatus.p1')}
          </Text>
        </View>

        {/* ── SECTION 7 : FAQ ── */}
        <View style={[styles.section, isWebLarge && styles.sectionWeb]}>
          <Chip label={t('commentCaMarcheScreen.faq.chip')} color={P.purple} bg={P.purpleSoft} />
          <Text style={styles.sectionTitle}>{t('commentCaMarcheScreen.faq.title')}</Text>
          <View style={styles.faqWrap}>
            <FaqItem
              q={t('commentCaMarcheScreen.faq.q1.question')}
              a={t('commentCaMarcheScreen.faq.q1.answer')}
            />
            <FaqItem
              q={t('commentCaMarcheScreen.faq.q2.question')}
              a={t('commentCaMarcheScreen.faq.q2.answer')}
            />
            <FaqItem
              q={t('commentCaMarcheScreen.faq.q3.question')}
              a={t('commentCaMarcheScreen.faq.q3.answer')}
            />
            <FaqItem
              q={t('commentCaMarcheScreen.faq.q4.question')}
              a={t('commentCaMarcheScreen.faq.q4.answer')}
            />
          </View>
        </View>

        {/* ── CTA final ── */}
        <View style={[styles.section, isWebLarge && styles.sectionWeb, { alignItems: isWebLarge ? 'center' : 'flex-start' }]}>
          <TouchableOpacity onPress={go} activeOpacity={0.88} style={isWebLarge ? { width: 320 } : { width: '100%' }}>
            <LinearGradient colors={[P.gold, '#D97706']} style={styles.cta} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}>
              <Text style={styles.ctaTxt}>{t('commentCaMarcheScreen.cta')}</Text>
              <Ionicons name="arrow-forward-circle" size={20} color="#fff" />
            </LinearGradient>
          </TouchableOpacity>
        </View>

        <View style={{ height: 60 }} />
      </ScrollView>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: P.bg },
  scrollContent: { paddingBottom: 0 },

  hero: { paddingHorizontal: 24, paddingTop: 64, paddingBottom: 48 },
  navLogoRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 28 },
  navBrand: { fontSize: 20, fontWeight: '900', color: '#fff' },
  heroTitle: { fontSize: 34, fontWeight: '900', color: '#fff', lineHeight: 40, marginBottom: 18, letterSpacing: -0.5 },
  heroSub: { fontSize: 15, color: 'rgba(255,255,255,0.82)', lineHeight: 24 },

  section: { paddingHorizontal: 24, paddingVertical: 40 },
  sectionWeb: { paddingHorizontal: 64, maxWidth: 900, alignSelf: 'center', width: '100%' },
  sectionDark: { backgroundColor: '#0E0017' },
  sectionPadDark: { paddingHorizontal: 24, paddingVertical: 44 },

  chip: { alignSelf: 'flex-start', borderRadius: 24, paddingHorizontal: 16, paddingVertical: 7, marginBottom: 16 },
  chipTxt: { fontSize: 11, fontWeight: '800', letterSpacing: 1.2 },

  sectionTitle: { fontSize: 28, fontWeight: '900', color: P.text, marginBottom: 16, letterSpacing: -0.5 },
  sectionTitleLight: { fontSize: 28, fontWeight: '900', color: '#F5F0FF', marginBottom: 16, letterSpacing: -0.5 },
  sectionText: { fontSize: 15, color: P.textSub, lineHeight: 25, marginBottom: 14 },
  sectionTextLight: { fontSize: 15, color: '#B9A8D9', lineHeight: 25, marginBottom: 14 },

  stepsWrap: { marginTop: 12, gap: 20 },
  stepBlock: { flexDirection: 'row', gap: 14 },
  stepNumberCircle: {
    width: 32, height: 32, borderRadius: 16, backgroundColor: P.purple,
    justifyContent: 'center', alignItems: 'center', flexShrink: 0,
  },
  stepNumberTxt: { color: '#fff', fontWeight: '800', fontSize: 14 },
  stepTitle: { fontSize: 15, fontWeight: '800', color: P.text, marginBottom: 4 },
  stepDesc: { fontSize: 13.5, color: P.textSub, lineHeight: 21 },

  discGrid: { marginTop: 20, gap: 14 },
  discCard: {
    backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 16, padding: 18,
  },
  discIconWrap: {
    width: 40, height: 40, borderRadius: 12, borderWidth: 1, backgroundColor: 'rgba(255,255,255,0.06)',
    justifyContent: 'center', alignItems: 'center', marginBottom: 10,
  },
  discLabel: { fontSize: 15, fontWeight: '800', color: '#fff', marginBottom: 2 },
  discTagline: { fontSize: 11, fontWeight: '600', marginBottom: 8 },
  discDesc: { fontSize: 12.5, color: '#9B82C4', lineHeight: 19 },

  faqWrap: { marginTop: 8, gap: 10 },
  faqItem: {
    backgroundColor: P.white, borderRadius: 14, padding: 16,
    borderWidth: 1, borderColor: P.border,
  },
  faqHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  faqQ: { flex: 1, fontSize: 14.5, fontWeight: '700', color: P.text },
  faqA: { fontSize: 13.5, color: P.textSub, lineHeight: 21, marginTop: 10 },

  cta: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 10, paddingVertical: 18, borderRadius: 16,
  },
  ctaTxt: { color: '#fff', fontSize: 15, fontWeight: '800' },
});

// ── Styles du sélecteur de langue flottant (fond sombre, cohérent avec le hero) ──
const langStyles = StyleSheet.create({
  trigger: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 54 : 30,
    right: 16,
    zIndex: 200,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  flag: { fontSize: 14 },
  label: { color: '#fff', fontSize: 12, fontWeight: '700' },
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 199,
  },
  menu: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 96 : 72,
    right: 16,
    backgroundColor: '#1A0840',
    borderRadius: 14,
    paddingVertical: 6,
    minWidth: 130,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    shadowColor: 'rgba(0,0,0,0.5)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 10,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  menuItemActive: { backgroundColor: 'rgba(245,158,11,0.12)' },
  menuFlag: { fontSize: 15 },
  menuLabel: { flex: 1, fontSize: 13, fontWeight: '600', color: '#fff' },
});
